<script setup lang="ts">
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import { useFormBuilderStore } from '../../../model/form-builder.store';
  import { getUsedFillTitleTypes } from '../../../lib/fill-title-utils';
  import type {
    FillTitleProperties as FillTitlePropsType,
    FillTitleType,
  } from '../../../model/form.types';
  import * as z from 'zod';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@/shared/ui';
  import { useFormBuilderToast } from '../../../composables/useFormBuilderToast';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  const formBuilderStore = useFormBuilderStore();
  const { showErrorToast } = useFormBuilderToast();

  type FormValues = {
    fillTitleType?: FillTitleType;
    auxiliaryText?: string;
    label?: string;
    labelPosition?: 'top' | 'left';
  };

  const FILL_TITLE_OPTIONS: { value: FillTitleType; label: string }[] = [
    { value: 'FIRST_TITLE', label: '1º título' },
    { value: 'SECOND_TITLE', label: '2º título' },
    { value: 'THIRD_TITLE', label: '3º título' },
    { value: 'FOURTH_TITLE', label: '4º título' },
  ];

  function onFillTitleTypeChange(newType: FillTitleType, handleChange: (v: FillTitleType) => void) {
    const usedTypes = getUsedFillTitleTypes(formBuilderStore.elements, props.elementId);
    if (usedTypes.has(newType)) {
      showErrorToast({
        title: 'Este tipo já está em uso por outro campo "Título preenchimento".',
        type: 'error',
        hideIcon: true,
      });
      return;
    }
    handleChange(newType);
  }

  function onFillTitleTypeInputChange(event: Event, handleChange: (v: FillTitleType) => void) {
    const input = event.target as HTMLInputElement;
    onFillTitleTypeChange(input.value as FillTitleType, handleChange);
  }

  const { values } = usePropertyForm<FormValues, FillTitlePropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      fillTitleType: z
        .enum(['FIRST_TITLE', 'SECOND_TITLE', 'THIRD_TITLE', 'FOURTH_TITLE'])
        .optional(),
      auxiliaryText: z.string().max(FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT).optional(),
      label: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LABEL).optional(),
      labelPosition: z.enum(['top', 'left']).optional(),
    }),
    extractInitialValues: (properties: FillTitlePropsType | undefined): FormValues => ({
      fillTitleType: properties?.fillTitleType ?? 'FIRST_TITLE',
      auxiliaryText: properties?.auxiliaryText ?? '',
      label: properties?.label ?? '',
      labelPosition: properties?.labelPosition ?? 'top',
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: FillTitlePropsType) => ({
      properties: {
        ...currentProperties,
        fillTitleType: values.fillTitleType ?? 'FIRST_TITLE',
        auxiliaryText: values.auxiliaryText || undefined,
        label: values.label || undefined,
        labelPosition: values.labelPosition ?? 'top',
      },
    }),
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ handleChange }"
        name="fillTitleType"
      >
        <FormItem class="flex flex-col gap-3">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Tipo do campo:
          </FormLabel>
          <div class="flex flex-col gap-3">
            <label
              v-for="option in FILL_TITLE_OPTIONS"
              :key="option.value"
              class="inline-flex items-center cursor-pointer gap-2"
            >
              <input
                type="radio"
                :name="`fillTitleType-${props.elementId ?? 'unknown'}`"
                :value="option.value"
                :checked="values.fillTitleType === option.value"
                class="sr-only"
                @change="onFillTitleTypeInputChange($event, handleChange)"
              >
              <span
                class="w-5 h-5 inline-block rounded-full border shrink-0"
                :class="
                  values.fillTitleType === option.value
                    ? 'border-[6px] border-[var(--theme-secondary-ink)]'
                    : 'border-light-gray-xx bg-white-01'
                "
              />
              <span class="text-sm text-gray-11">{{ option.label }}</span>
            </label>
          </div>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="auxiliaryText"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
